import { collection, doc, getDoc, getDocs, setDoc, writeBatch } from 'firebase/firestore';
import { db } from './firebase.utils';
import { SALE_KIND, SALE_STATUSES } from '../sales/sales.constants';
import { transactionDoc } from './transactionPaths';

const BATCH_LIMIT = 400;

const stagedStatus = (status) => {
  const value = String(status ?? '').toLowerCase();
  if (!value || value === 'staged') return SALE_STATUSES.STAGED;
  return status;
};

async function commitDeletes(refs) {
  for (let i = 0; i < refs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    refs.slice(i, i + BATCH_LIMIT).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
}

async function copyQueueCollection(uid, sourceName, platform) {
  const snap = await getDocs(collection(db, 'users', uid, sourceName));
  const copied = [];

  for (const source of snap.docs) {
    const dest = transactionDoc(uid, source.id);
    const existing = await getDoc(dest);
    if (!existing.exists()) {
      await setDoc(dest, {
        ...source.data(),
        kind: SALE_KIND,
        platform,
        status: stagedStatus(source.data().status),
      });
    }
    copied.push(source.ref);
  }

  await commitDeletes(copied);
}

async function copySubcollection(uid, saleId, name) {
  const snap = await getDocs(collection(db, 'users', uid, 'sales', saleId, name));
  for (const source of snap.docs) {
    const dest = doc(db, 'users', uid, 'transactions', saleId, name, source.id);
    const existing = await getDoc(dest);
    if (!existing.exists()) await setDoc(dest, source.data());
  }
  return snap.docs.map((source) => source.ref);
}

async function copySales(uid) {
  const snap = await getDocs(collection(db, 'users', uid, 'sales'));
  const copied = [];

  for (const source of snap.docs) {
    const dest = transactionDoc(uid, source.id);
    const existing = await getDoc(dest);
    if (!existing.exists()) {
      await setDoc(dest, {
        ...source.data(),
        kind: source.data().kind || SALE_KIND,
      });
    }
    const cardRefs = await copySubcollection(uid, source.id, 'cards');
    const supplyRefs = await copySubcollection(uid, source.id, 'supplies');
    copied.push(...cardRefs, ...supplyRefs, source.ref);
  }

  await commitDeletes(copied);
}

/**
 * Copies ebay, whatnot, and sales into users/{uid}/transactions once.
 * Safe to call on every visit: a user flag stops the second run, and a
 * rerun after a partial copy skips documents that already arrived.
 */
export async function migrateLegacyQueues(uid) {
  if (!uid) return;
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists() && userSnap.data().transactionsMigrated) return;

  await copyQueueCollection(uid, 'ebay', 'eBay');
  await copyQueueCollection(uid, 'whatnot', 'Whatnot');
  await copySales(uid);

  await setDoc(userRef, { transactionsMigrated: true }, { merge: true });
}
